import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Facebook, Instagram, Linkedin, Youtube, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { VERTICALS, CONTACT } from "@/lib/data";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email");
    } finally { setLoading(false); }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-shramik-ink text-white overflow-hidden">
      <div className="orb bg-shramik-green/30 w-[520px] h-[520px] -top-40 -left-40" />
      <div className="orb bg-shramik-blue/30 w-[420px] h-[420px] -bottom-20 -right-20" />

      <div className="container-page relative pt-24 pb-10">
        {/* Big CTA */}
        <div className="border-b border-white/10 pb-16 mb-16">
          <div className="text-xs uppercase tracking-[0.28em] text-shramik-gold mb-6">Partner with us</div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
              Let's build a <em className="not-italic font-serif italic text-shramik-gold">better future</em> — together.
            </h2>
            <Link to="/contact" data-testid="footer-cta" className="inline-flex items-center gap-3 rounded-full bg-white text-shramik-ink px-8 py-4 text-sm font-semibold hover:bg-shramik-gold hover:text-shramik-ink transition-all group shrink-0">
              Start a conversation <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-white p-1.5 flex items-center justify-center overflow-hidden">
                <img src="/logo.png" alt="Shramik Group" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-display font-bold text-xl">SHRAMIK</div>
                <div className="text-[10px] tracking-[0.28em] font-semibold text-shramik-gold uppercase">Group</div>
              </div>
            </Link>
            <p className="text-sm text-white/70 max-w-sm leading-relaxed">
              A diversified enterprise driving rural prosperity through agriculture, dairy, construction, petroleum and community development.
            </p>
            <div className="flex gap-3 mt-6">
              {[Facebook, Instagram, Linkedin, Youtube].map((Ic, i) => (
                <a key={i} href="#" aria-label="social" className="w-10 h-10 rounded-full border border-white/15 hover:bg-white hover:text-shramik-ink flex items-center justify-center transition-colors">
                  <Ic className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-shramik-gold mb-4">Quick Links</div>
            <ul className="space-y-2 text-sm text-white/80">
              {[["About","/about"],["Leadership","/leadership"],["Upcoming Projects","/projects"],["Gallery","/gallery"],["Careers","/careers"],["News","/news"]].map(([l,to]) => (
                <li key={to}><Link to={to} className="hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-shramik-gold mb-4">Businesses</div>
            <ul className="space-y-2 text-sm text-white/80">
              {VERTICALS.map(v => (
                <li key={v.slug}><Link to={`/businesses/${v.slug}`} className="hover:text-white transition-colors">{v.name}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-shramik-gold mb-4">Reach us</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-shramik-gold"/> <a href={`tel:${CONTACT.phoneRaw}`}>{CONTACT.phone}</a></li>
              <li className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-shramik-gold"/> <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></li>
              <li className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-shramik-gold"/> <span>{CONTACT.address}</span></li>
            </ul>
          </div>
        </div>

        <form onSubmit={subscribe} className="mt-14 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between border-t border-white/10 pt-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-shramik-gold mb-1">Newsletter</div>
            <div className="text-lg font-display">Rural insights, group updates. Once a month.</div>
          </div>
          <div className="flex w-full md:w-auto gap-3">
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" required
              data-testid="newsletter-email"
              className="w-full md:w-80 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-sm placeholder:text-white/50 focus:outline-none focus:border-shramik-gold"
            />
            <button data-testid="newsletter-submit" disabled={loading} className="rounded-full bg-shramik-gold text-shramik-ink px-6 py-3 text-sm font-semibold hover:bg-white disabled:opacity-70">
              {loading ? "..." : "Subscribe"}
            </button>
          </div>
        </form>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Shramik Group. All rights reserved.</div>
          <div className="flex gap-5"><span>Privacy</span><span>Terms</span><span>Sitemap</span></div>
        </div>
      </div>
    </footer>
  );
}
